import { LANGUAGE_OPTIONS } from './list';
import { SettingsType } from './settings';
import { Visibility } from './visibility';
import { PersonMember } from './person-member';
import { Widget } from './widget';
import { BlockedService, Service } from './service';
import { UserDatacenterView } from './datacenter-aggrid-view';
import { BocAvailableValue } from './boc-available-value';
import { OrganizationRight } from './organization-right';

export class User {
	constructor(
		public id: number,
		public firstname: string,
		public lastname: string,
		public gender: string,
		public language: keyof typeof LANGUAGE_OPTIONS,
		public email: string,
		public phone: string,
		public city: string,
		public currentVisibility: Visibility,
		public internal: false,
		public visibilities: Visibility[],
		public services: Service[],
		public settings: SettingsType[],
		public widgets: Widget[],
		public soldTos: string[],
		public shipTos: string[],
		public payers: string[],
		public previousLastConnectionDate: string,
		public datacenterViews: UserDatacenterView[],
		public visibleOrganizations: PersonMember[],
		public soldToCallableAtReshipper: string[],
		public organizationRights: OrganizationRight[],
		public simulatorUser?: User,
		public readOnly?: boolean,
		public bocAvailableValues?: BocAvailableValue[],
		public dateFormat?: string,
		public timeFormat?: string,
		public initialOrganizationPersonMember?: PersonMember,
		public blockedServices?: BlockedService[]
	) {}

	isSimulatingOrDelegating() {
		return this.simulatorUser !== undefined;
	}

	isDelegating() {
		return this.simulatorUser !== undefined && this.simulatorUser.id === this.id;
	}

	isSimulating() {
		return this.simulatorUser !== undefined && this.simulatorUser.id !== this.id;
	}

	getSettingsVisibilityGroupId() {
		return this.simulatorUser !== undefined && this.simulatorUser.id === this.id
			? this.simulatorUser.currentVisibility.id : this.currentVisibility.id;
	}

	hasService(serviceCode: string) {
		return this.getService(serviceCode) !== undefined;
	}

	isServiceBlocked(serviceCode: string) {
		return this.blockedServices?.some(v => v.code.toUpperCase() === serviceCode) ?? false;
	}

	hasServiceParameterEqualsTo(serviceCode: string, paramName: string, paramValue: string) {
		const service = this.getService(serviceCode);
		return service !== undefined && Boolean(service.parameters)
			? service.parameters.some(p => p.name.toLowerCase() === paramName.toLowerCase()
				&& p.value.toLowerCase().includes(paramValue.toLowerCase()))
			: false;
	}

	getServiceParameterValue(serviceCode: string, paramName: string): string | undefined {
		const service = this.getService(serviceCode);
		if (!service?.parameters) {
			return undefined;
		}
		const parameter = service.parameters.find(p => p.name.toLowerCase() === paramName.toLowerCase());
		return parameter?.value;
	}

	getService(serviceCode: string) {
		return this.services.find(v => v.code.toUpperCase() === serviceCode);
	}

	isClaimDetector() {
		return this.isClaimRole('quapp.detector');
	}

	isClaimValidator() {
		return this.isClaimRole('quapp.validator');
	}

	isClaimStrictlyReader() {
		const service = this.getService('QUAPP');
		return service !== undefined
			&& this.isClaimReader()
			&& service.roles.length === 1;
	}

	isClaimReader() {
		return this.isClaimRole('quapp.reader');
	}

	isClaimRole(role: string) {
		return this.isRole('QUAPP', role);
	}

	isOfferValidator() {
		return this.isOfferRole('eoffr.validator');
	}

	isOrderRole(role: string) {
		return this.isRole('EORDR', role);
	}

	isOfferRole(role: string) {
		return this.isRole('EOFFR', role);
	}

	isRole(serviceCode: string, role: string) {
		const service = this.getService(serviceCode);
		return service?.roles.includes(role) ?? false;
	}

	isOfferSubmitter() {
		const service = this.getService('EOFFR');
		if (service === undefined) {
			return false;
		}
		const { roles } = service;
		return roles.length === 0 || roles.includes('eoffr.validator');
	}

	getSettings<T1 extends SettingsType, T2 extends T1['key'], T3 extends Extract<T1, { key: T2 }>>(key: T2) {
		return this.settings.find((v): v is T3 => v.key === key);
	}

	hasAccessToOrganizations(memberId: number) {
		return this.organizationRights.some(oR => oR.memberId === memberId);
	}

	canUpdateOrganization(memberId: number) {
		return this.organizationRights.some(oR => oR.memberId === memberId && oR.canManageInformation);
	}

	canUpdateOrganizationServices(memberId: number) {
		return this.organizationRights.some(oR => oR.memberId === memberId && oR.canManageServices);
	}

	canUpdateOrganizationVisibilities(memberId: number) {
		return this.organizationRights.some(oR => oR.memberId === memberId && oR.canManageVisibilities);
	}

	canUpdateOrganizationUsers(memberId: number) {
		return this.organizationRights.some(oR => oR.memberId === memberId && oR.canManageUsers);
	}
}
